import { BlackPopupRolagem } from "@/components/blog/BlackPopupRolagem";
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Star } from "lucide-react";

import { SiteHeader } from "@/components/feo/SiteHeader";
import { BlackDestaqueSection } from "@/components/black2x1/BlackDestaqueSection";
import { SiteFooter } from "@/components/feo/SiteFooter";
import { Button } from "@/components/ui/button";
import { DEPOIMENTOS } from "@/lib/depoimentos/data";

export const Route = createFileRoute("/depoimentos")({
  head: () => {
    const total = DEPOIMENTOS.length;
    const media =
      DEPOIMENTOS.reduce((s, d) => s + d.rating, 0) / Math.max(total, 1);
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "EducationalOrganization",
      name: "Fisio em Ortopedia",
      url: "https://fisioemortopedia.com.br",
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: media.toFixed(1),
        bestRating: "5",
        ratingCount: total,
      },
      review: DEPOIMENTOS.slice(0, 20).map((d) => ({
        "@type": "Review",
        author: { "@type": "Person", name: d.author_name },
        datePublished: d.review_created_at,
        reviewBody: d.comment,
        reviewRating: {
          "@type": "Rating",
          ratingValue: d.rating,
          bestRating: "5",
        },
      })),
    };
    return {
      meta: [
        { title: "Depoimentos de Alunos | Fisio em Ortopedia" },
        {
          name: "description",
          content:
            "Depoimentos e avaliações verificadas do Google de fisioterapeutas sobre as pós-graduações e cursos da Fisio em Ortopedia (FEO).",
        },
        { property: "og:title", content: "Depoimentos de Alunos FEO" },
        {
          property: "og:description",
          content:
            "Depoimentos reais de fisioterapeutas sobre as pós-graduações e cursos da FEO.",
        },
        { property: "og:url", content: "https://fisioemortopedia.com.br/depoimentos" },
        { property: "og:type", content: "website" },
        { name: "twitter:title", content: "Depoimentos de Alunos FEO" },
        { name: "twitter:description", content: "Depoimentos reais de alunos e ex-alunos FEO." },

      ],
      links: [{ rel: "canonical", href: "https://fisioemortopedia.com.br/depoimentos" }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(jsonLd),
        },
      ],
    };
  },
  component: DepoimentosPage,
});

function Stars({ n }: { n: number }) {
  return (
    <div className="flex items-center gap-0.5">
      <span className="sr-only">{n} de 5 estrelas</span>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          aria-hidden="true"
          className={
            i < n
              ? "h-4 w-4 fill-amber-400 text-amber-400"
              : "h-4 w-4 fill-slate-200 text-slate-200"
          }
        />
      ))}
    </div>
  );
}

function DepoimentosPage() {
  const [visible, setVisible] = useState(48);
  const depoimentos = DEPOIMENTOS;

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />

      {/* HERO */}
      <section className="bg-gradient-to-br from-[#13123a] via-[#1a1958] to-[#13123a] text-white">
        <div className="container mx-auto px-6 py-16 md:py-24 text-center">
          <div className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-white/90 mb-6">
            avaliações reais do Google
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            O que diz quem já<br className="hidden md:block" /> estudou na FEO
          </h1>
          <p className="mt-6 text-lg text-white/80 max-w-2xl mx-auto">
            Depoimentos reais de fisioterapeutas que fizeram<br className="hidden md:block" /> pós-graduação e cursos na Fisio em Ortopedia.
          </p>
        </div>
      </section>

      {/* GRID */}
      <section className="bg-white">
        <div className="container mx-auto px-6 py-16">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {depoimentos.slice(0, visible).map((d) => (
              <article
                key={d.id}
                className="min-w-0 [overflow-wrap:anywhere] rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition flex flex-col"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-slate-600 font-semibold shrink-0">
                    {d.author_name.slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{d.author_name}</div>
                    <Stars n={d.rating} />
                  </div>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line flex-1">
                  {d.comment}
                </p>
              </article>
            ))}
          </div>

          {depoimentos.length > visible ? (
            <div className="mt-10 text-center">
              <Button
                onClick={() => setVisible((v) => v + 48)}
                variant="outline"
                className="border-slate-300"
              >
                Ver mais depoimentos ({depoimentos.length - visible} restantes)
              </Button>
            </div>
          ) : null}
        </div>
      </section>

      <BlackDestaqueSection origem="depoimentos" />

      <BlackPopupRolagem origem="depoimentos" />
      <SiteFooter />
    </div>
  );
}
